import { BlockOutlined, ChevronRight } from '@mui/icons-material';
import {
    Avatar,
    CardContent,
    Chip,
    CircularProgress,
    Grid,
    IconButton,
    ListItemAvatar,
    ListItemText,
    Pagination,
    Paper,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
} from '@mui/material';
import Card from '@mui/material/Card';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Typography from '@mui/material/Typography';
import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useFetch } from '../../../hooks/useFecth';
import DialogOffer from './components/DialogOffer';
import useDialog from '../../../hooks/useDialog';
import SelectBrandLomadee from './components/SelectBrandLomadee';

// Helper function to format dates
const formatDate = dateString => {
    if (!dateString) return 'Sem prazo';
    try {
        const date = new Date(dateString);
        return date.toLocaleDateString('pt-BR');
    } catch {
        return 'Data inválida';
    }
};

export default function CampaignsSocialSoul() {
    const [searchParams, setParams] = useSearchParams();

    const [campaign, setCampaign] = useState();
    const [brand, setBrand] = useState();
    const [campaignType, setCampaignType] = useState(searchParams.get('type') || 'all');
    
    const { handleOpen } = useDialog('offer');

    const { data, mutate, isLoading } = useFetch(brand?.id ? 'brokers/lomadee/campaigns' : null, {
        params: {
            ...Object.fromEntries(searchParams),
            organizationIds: brand?.id,
            ...(campaignType !== 'all' && { type: campaignType }),
        },
    });

    const handleClearFilters = () => {
        setParams({});
        setCampaignType('all');
    };

    const handlePage = (event, page) => {
        setParams(prevParams => {
            const newParams = new URLSearchParams(prevParams);
            newParams.set('page', page);
            return newParams;
        });
    };

    const handleTypeChange = (event, newType) => {
        if (newType !== null) {
            setCampaignType(newType);
            setParams(prevParams => {
                const newParams = new URLSearchParams(prevParams);
                if (newType !== 'all') {
                    newParams.set('type', newType);
                } else {
                    newParams.delete('type');
                }
                newParams.set('page', 1);
                return newParams;
            });
        }
    };

    const openCampaignSyncModal = campaign => {
        handleOpen();
        campaign && setCampaign(campaign);
    };

    const getCampaignTypeLabel = type => {
        switch (type) {
            case 'PersonalCoupon':
                return 'Cupom Pessoal';
            case 'GenericCoupon':
                return 'Cupom Genérico';
            case 'Offer':
                return 'Oferta';
            default:
                return type;
        }
    };

    return (
        <Container maxWidth="xl">
            <Stack pb={2}>
                <Typography variant="h2">Campanhas Lomadee</Typography>
                <Typography variant="body2" color="text.secondary">
                    Gerencie campanhas, cupons e ofertas da rede Lomadee
                </Typography>
            </Stack>

            <Card>
                <CardContent>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12}>
                            <SelectBrandLomadee
                                value={brand?.id || ''}
                                name="brandId"
                                label="Selecione a marca para ver as campanhas"
                                onChange={selectedBrand => {
                                    setBrand(selectedBrand);
                                    setParams(prevParams => {
                                        const newParams = new URLSearchParams(prevParams);
                                        if (selectedBrand?.id) {
                                            newParams.set('organizationIds', selectedBrand.id);
                                        } else {
                                            newParams.delete('organizationIds');
                                        }
                                        newParams.set('page', 1);
                                        return newParams;
                                    });
                                }}
                            />
                        </Grid>

                        {brand && (
                            <Grid item xs={12}>
                                <ToggleButtonGroup value={campaignType} exclusive onChange={handleTypeChange} fullWidth size="small">
                                    <ToggleButton value="all">Todas</ToggleButton>
                                    <ToggleButton value="GenericCoupon">Cupons Genéricos</ToggleButton>
                                    <ToggleButton value="PersonalCoupon">Cupons Pessoais</ToggleButton>
                                    <ToggleButton value="Offer">Ofertas</ToggleButton>
                                </ToggleButtonGroup>
                            </Grid>
                        )}
                    </Grid>
                </CardContent>
                <Divider />

                <List size="md" sx={{ gap: 1 }}>
                    <>
                        {!Boolean(brand) && (
                            <Paper sx={{ p: 4, border: 0 }}>
                                <Stack direction="row" justifyContent="center" gap={1}>
                                    <Typography>Selecione uma marca para ver as campanhas.</Typography>
                                </Stack>
                            </Paper>
                        )}

                        {Boolean(brand) && data?.meta?.total === 0 && !isLoading && (
                            <Paper sx={{ p: 4, border: 0 }}>
                                <Stack direction="row" justifyContent="center" gap={1}>
                                    <Typography>Nenhuma campanha encontrada.</Typography>
                                    <BlockOutlined />
                                </Stack>
                            </Paper>
                        )}

                        {isLoading && (
                            <Paper sx={{ p: 4, border: 0 }}>
                                <Stack justifyContent="center" gap={1}>
                                    <CircularProgress size={20} />
                                    <Typography>Carregando campanhas...</Typography>
                                </Stack>
                            </Paper>
                        )}

                        {Boolean(brand) &&
                            data?.meta?.total > 0 &&
                            data?.campaigns
                                ?.filter(item => !data?.syncedCampaigns?.includes(item.id))
                                ?.map(item => (
                                    <React.Fragment key={`campaign_item_${item.id}`}>
                                        <ListItem
                                            secondaryAction={
                                                <IconButton onClick={() => openCampaignSyncModal(item)}>
                                                    <ChevronRight />
                                                </IconButton>
                                            }
                                        >
                                            <ListItemAvatar sx={{ mr: 2 }}>
                                                <Avatar
                                                    src={item.image}
                                                    variant="rounded"
                                                    sx={{ border: '1px solid #eee', objectFit: 'cover', width: 50, height: 50 }}
                                                />
                                            </ListItemAvatar>
                                            <ListItemText
                                                primary={item.name || item.description || 'Campanha sem nome'}
                                                primaryTypographyProps={{ variant: 'title-sm' }}
                                                secondary={
                                                    <Stack spacing={0.5} pt={0.5}>
                                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }} gutterBottom>
                                                            {item.description}
                                                        </Typography>
                                                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 0.5 }}>
                                                            <Chip size="small" label={`ID: ${item.id}`} />
                                                            <Chip
                                                                size="small"
                                                                label={getCampaignTypeLabel(item.type)}
                                                                color={
                                                                    item.type === 'Offer'
                                                                        ? 'primary'
                                                                        : item.type === 'GenericCoupon'
                                                                        ? 'secondary'
                                                                        : 'default'
                                                                }
                                                            />
                                                            {item.category?.name && <Chip size="small" label={item.category.name} />}
                                                            {item.code && (
                                                                <Chip
                                                                    size="small"
                                                                    label={`Código: ${item.code}`}
                                                                    variant="outlined"
                                                                    color="success"
                                                                />
                                                            )}
                                                            {item.discountValue && (
                                                                <Chip size="small" label={`${item.discountValue}% OFF`} color="error" />
                                                            )}
                                                            {item.highlighted && <Chip size="small" label="Destaque" color="warning" />}
                                                            {item.status && <Chip size="small" label="Ativa" color="success" variant="outlined" />}
                                                        </Stack>
                                                        <Typography variant="caption" color="text.secondary">
                                                            Válido até: {formatDate(item.validUntil)}
                                                        </Typography>
                                                    </Stack>
                                                }
                                            />
                                        </ListItem>
                                        <Divider />
                                    </React.Fragment>
                                ))}
                    </>
                </List>

                {Boolean(brand) && data?.meta?.total > 0 && (
                    <Stack direction="row" py={2} justifyContent="center">
                        <Pagination page={data?.meta?.page || 1} count={data?.meta?.totalPages || 1} onChange={handlePage} />
                    </Stack>
                )}
            </Card>

            <DialogOffer mutate={mutate} campaign={campaign} />
        </Container>
    );
}
